import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import {
  createCompanyDepartment,
  createMenuCategory,
  createOutletAddon,
  createOutletMenuItem,
  deleteMenuCategory,
  deleteOutletAddon,
  deleteOutletMenuItem,
  updateDepartment,
  updateMenuCategory,
  updateOutletAddon,
  updateOutletMenuItem,
} from '@/renderer/services/menuService';
import { apiError, apiSuccess } from '@/renderer/util/notify';
import type {
  CreateAddonRequest,
  CreateCategoryRequest,
  CreateMenuItemRequest,
  UpdateAddonRequest,
  UpdateCategoryRequest,
  UpdateMenuItemRequest,
  MenuCategory,
} from '@/renderer/types/menu';
import type {
  CreateDepartmentRequest,
  UpdateDepartmentRequest,
} from '@/renderer/types/department';

function invalidateAll(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: queryKeys.menu.all });
}

const sortCategories = (categories: MenuCategory[]) =>
  [...categories].sort(
    (left, right) =>
      (left.display_order ?? 0) - (right.display_order ?? 0) ||
      left.name.localeCompare(right.name),
  );

function upsertCategoryInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  category: MenuCategory,
) {
  queryClient.setQueryData<MenuCategory[]>(
    queryKeys.menu.categories(category.outlet_id),
    (current = []) =>
      sortCategories([
        ...current.filter((entry) => entry.id !== category.id),
        category,
      ]),
  );
}

function removeCategoryFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  categoryId: string,
) {
  queryClient.setQueriesData<MenuCategory[]>(
    { queryKey: ['menu', 'categories'] },
    (current) => {
      if (!Array.isArray(current)) return current;
      return current.filter((entry) => entry.id !== categoryId);
    },
  );
}

// ── Departments ───────────────────────────────────────────────────────────────

export function useCreateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateDepartmentRequest) => {
      const res = await createCompanyDepartment(request);
      if (!res.success || !res.data)
        throw new Error(res.error ?? 'Failed to create department');
      return res.data;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Department created successfully');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: UpdateDepartmentRequest) => {
      const res = await updateDepartment(request);
      if (!res.success || !res.data)
        throw new Error(res.error ?? 'Failed to update department');
      return res.data;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Department updated successfully');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

// ── Categories ────────────────────────────────────────────────────────────────

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateCategoryRequest) => {
      const res = await createMenuCategory(request);
      if (!res.success || !res.data)
        throw new Error(res.error ?? 'Failed to create category');
      return res.data;
    },
    onSuccess: (category) => {
      upsertCategoryInCache(queryClient, category);
      queryClient.invalidateQueries({
        queryKey: queryKeys.menu.menuCounts(category.outlet_id),
      });
      apiSuccess('Category created');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: UpdateCategoryRequest) => {
      const res = await updateMenuCategory(request);
      if (!res.success || !res.data)
        throw new Error(res.error ?? 'Failed to update category');
      return res.data;
    },
    onSuccess: (category) => {
      upsertCategoryInCache(queryClient, category);
      queryClient.invalidateQueries({ queryKey: ['menu', 'items-list'] });
      apiSuccess('Category updated');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (categoryId: string) => {
      const res = await deleteMenuCategory(categoryId);
      if (!res.success)
        throw new Error(res.error ?? 'Failed to delete category');
      return categoryId;
    },
    onSuccess: (categoryId) => {
      removeCategoryFromCache(queryClient, categoryId);
      queryClient.invalidateQueries({ queryKey: ['menu', 'items-list'] });
      queryClient.invalidateQueries({ queryKey: ['menu', 'counts'] });
      apiSuccess('Category deleted');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

// ── Menu Items ────────────────────────────────────────────────────────────────

export function useCreateMenuItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateMenuItemRequest) => {
      const res = await createOutletMenuItem(request);
      if (!res.success || !res.data)
        throw new Error(res.error ?? 'Failed to create menu item');
      return res.data;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Menu item created');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

export function useUpdateMenuItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: UpdateMenuItemRequest) => {
      const res = await updateOutletMenuItem(request);
      if (!res.success || !res.data)
        throw new Error(res.error ?? 'Failed to update menu item');
      return res.data;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Menu item updated');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

export function useDeleteMenuItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: string) => {
      const res = await deleteOutletMenuItem(itemId);
      if (!res.success)
        throw new Error(res.error ?? 'Failed to delete menu item');
      return itemId;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Menu item deleted');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

// ── Addons ────────────────────────────────────────────────────────────────────

export function useCreateAddon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateAddonRequest) => {
      const res = await createOutletAddon(request);
      if (!res.success || !res.data)
        throw new Error(res.error ?? 'Failed to create addon');
      return res.data;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Addon created');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

export function useUpdateAddon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: UpdateAddonRequest) => {
      const res = await updateOutletAddon(request);
      if (!res.success || !res.data)
        throw new Error(res.error ?? 'Failed to update addon');
      return res.data;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Addon updated');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

export function useDeleteAddon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (addonId: string) => {
      const res = await deleteOutletAddon(addonId);
      if (!res.success) throw new Error(res.error ?? 'Failed to delete addon');
      return addonId;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Addon deleted');
    },
    onError: (err: Error) => apiError(err.message),
  });
}
